import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { ScreenableFieldSetupStepType } from './useScreenableFieldSetupNavigation';
import {
  InternalApiEntitySchemaField,
  InternalApiEntityType,
  InternalApiScreeningFlow,
  InternalApiScreeningListType,
  InternalApiScreeningMatchType,
} from '../../../api-internal';
import { GoodlistIdentifiers } from '../steps/GoodlistingStep';
import { ApiSanctionType } from '../../../api';
import { UsableField } from '../steps/ScreenableDataStep';
import { useQueryEntitySchema } from '../../../schema/entitySchemaActions';
import { getCustomListTypes, getDowJonesListTypes, getUsableFields } from './helper';
import { ScreenableFieldCustomListConfiguration } from '../steps/ListSelectionStep';

export const useScreenableFieldSetupContextManager = (
  entityType: InternalApiEntityType,
  flow: InternalApiScreeningFlow | undefined
): ScreenableFieldSetupProps => {
  const { data: schema } = useQueryEntitySchema(entityType);
  const [state, setState] = useState<ScreenableFieldSetupValue>(() =>
    flow && schema?.schemaFields
      ? createFromFlow(flow, schema.schemaFields, entityType)
      : createInitialValue(entityType)
  );

  useEffect(() => {
    if (!flow || !schema?.schemaFields) {
      return;
    }
    setState(createFromFlow(flow, schema.schemaFields, entityType));
  }, [flow, schema, entityType]);

  const updateValuePart = useCallback(
    (fieldName: keyof ScreenableFieldSetupValue, value: any) => {
      setState((old) => ({ ...old, [fieldName]: value }));
    },
    [setState]
  );

  return useMemo(() => {
    return {
      ...state,
      updateScreenableField: (value: UsableField[]) => updateValuePart('usableFields', value),
      updateGoodlistIdentifiers: (value: GoodlistIdentifiers) => updateValuePart('goodlistIdentifiers', value),
      updateIncludedSegments: (value: string[] | undefined) => updateValuePart('includedSegments', value),
      updateExcludedSegments: (value: string[] | undefined) => updateValuePart('excludedSegments', value),
      updateMatchingType: (value: { type: InternalApiScreeningMatchType; value?: number }) =>
        updateValuePart('matchType', value),
      updateDowJonesListTypes: (value: ApiSanctionType[]) => updateValuePart('dowJonesListTypes', value),
      updateSelectorGroupCodes: (value: string[] | undefined) => updateValuePart('selectorGroupCodes', value),
      updateCustomListConfiguration: (value: ScreenableFieldCustomListConfiguration[]) =>
        updateValuePart('customListConfiguration', value),
      completedSteps: calculateCompletedSteps(state),
    };
  }, [state, updateValuePart, entityType, flow]);
};

export type ScreenableFieldSetupValue = {
  flowId: string | undefined;
  entityType: InternalApiEntityType;
  usableFields: UsableField[];
  includedSegments: string[] | undefined;
  excludedSegments: string[] | undefined;
  goodlistIdentifiers: GoodlistIdentifiers;
  matchType: { type: InternalApiScreeningMatchType; value?: number };
  dowJonesListTypes: ApiSanctionType[];
  selectorGroupCodes: string[] | undefined;
  customListConfiguration: ScreenableFieldCustomListConfiguration[];
  // lists, etc
};

export type ScreenableFieldSetupProps = ScreenableFieldSetupValue & {
  updateScreenableField: (value: UsableField[]) => void;
  updateGoodlistIdentifiers: (value: GoodlistIdentifiers) => void;
  updateIncludedSegments: (value: string[] | undefined) => void;
  updateExcludedSegments: (value: string[] | undefined) => void;
  updateMatchingType: (value: { type: InternalApiScreeningMatchType; value?: number }) => void;
  updateDowJonesListTypes: (value: ApiSanctionType[]) => void;
  updateSelectorGroupCodes: (value: string[] | undefined) => void;
  updateCustomListConfiguration: (value: ScreenableFieldCustomListConfiguration[]) => void;
  completedSteps: Map<ScreenableFieldSetupStepType, 'completed' | 'skip' | undefined>;
};

const createFromFlow = (
  flow: InternalApiScreeningFlow,
  schemaFields: InternalApiEntitySchemaField[],
  entityType: InternalApiEntityType
): ScreenableFieldSetupProps => {
  return {
    flowId: flow.id,
    entityType: entityType,
    usableFields: getUsableFields(flow, schemaFields),
    includedSegments: flow.includedSegments,
    excludedSegments: flow.excludedSegments,
    goodlistIdentifiers: flow.goodList
      ? {
          goodlistKey1: flow.goodList.key1,
          goodlistKey2: flow.goodList.key2,
          goodlistKey3: flow.goodList.key3,
        }
      : {},
    matchType: {
      type: flow.match.type,
      value: flow.match.value,
    },
    dowJonesListTypes: getDowJonesListTypes(flow),
    selectorGroupCodes: flow?.lists
      ?.filter(({ type }) => type === InternalApiScreeningListType.SANCTION)
      .map(({ selectorGroupCodes }) => selectorGroupCodes ?? [])
      .flat(),
    customListConfiguration: getCustomListTypes(flow),
    // default, should never happen
    completedSteps: new Map(),
    updateScreenableField: () => () => {},
    updateGoodlistIdentifiers: () => () => {},
    updateIncludedSegments: () => () => {},
    updateExcludedSegments: () => () => {},
    updateMatchingType: () => () => {},
    updateDowJonesListTypes: () => () => {},
    updateSelectorGroupCodes: () => () => {},
    updateCustomListConfiguration: () => () => {},
  };
};

const createInitialValue = (entityType: InternalApiEntityType): ScreenableFieldSetupProps => {
  return {
    flowId: undefined,
    entityType: entityType,
    usableFields: [],
    includedSegments: [],
    excludedSegments: [],
    goodlistIdentifiers: {},
    matchType: {
      type: InternalApiScreeningMatchType.FUZZY_MATCH,
      value: 95,
    },
    dowJonesListTypes: [],
    selectorGroupCodes: [],
    customListConfiguration: [],
    // default, should never happen
    completedSteps: new Map(),
    updateScreenableField: () => () => {},
    updateGoodlistIdentifiers: () => () => {},
    updateIncludedSegments: () => () => {},
    updateExcludedSegments: () => () => {},
    updateMatchingType: () => () => {},
    updateDowJonesListTypes: () => () => {},
    updateSelectorGroupCodes: () => () => {},
    updateCustomListConfiguration: () => () => {},
  };
};

const calculateCompletedSteps = (state: ScreenableFieldSetupValue) => {
  const completedSteps = new Map();
  const excludedGroupsLength = (state.excludedSegments ?? []).length;
  if (state.usableFields.length > 0) {
    completedSteps.set('SCREENABLE_DATA', 'completed');
  }
  if (state.entityType === InternalApiEntityType.TRANSACTION) {
    completedSteps.set('GOODLIST', Object.values(state.goodlistIdentifiers).some((it) => !!it) ? 'completed' : 'skip');
  } else {
    completedSteps.delete('GOODLIST');
  }
  if (state.includedSegments) {
    completedSteps.set('INCLUDED_GROUPS', 'completed');
  }
  completedSteps.set('EXCLUDED_GROUPS', excludedGroupsLength > 0 ? 'completed' : 'skip');
  completedSteps.set('MATCHING_TYPE', state.matchType ? 'completed' : 'skip');
  if (areUsableFieldsValid(state.dowJonesListTypes, state.customListConfiguration)) {
    completedSteps.set('USABLE_DATA', 'completed');
  } else {
    completedSteps.delete('USABLE_DATA');
  }
  if (areSelectorCodesValid(state.dowJonesListTypes, state.selectorGroupCodes)) {
    completedSteps.set('SELECTOR_CODE', 'completed');
  } else {
    completedSteps.delete('SELECTOR_CODE');
  }

  return completedSteps;
};

const areUsableFieldsValid = (
  dowJonesListTypes: ApiSanctionType[],
  customLists: ScreenableFieldCustomListConfiguration[]
) => {
  const allCustomlistsHaveSelectedFields = customLists.every((it) => it.fields?.length > 0);
  if (dowJonesListTypes.length > 0) {
    return allCustomlistsHaveSelectedFields;
  } else {
    return customLists.length > 0 && allCustomlistsHaveSelectedFields;
  }
};

const areSelectorCodesValid = (dowJonesListTypes: ApiSanctionType[], selectorGroupCodes: string[] | undefined) => {
  if (dowJonesListTypes.includes(ApiSanctionType.SANCTION)) {
    return selectorGroupCodes?.length && selectorGroupCodes.length > 0;
  }
  return true;
};

export const ScreenableFieldSetupContext = createContext<ScreenableFieldSetupProps>({} as ScreenableFieldSetupProps);
